/**
 * shellCombatant - small read and write helpers the shell's hooks share.
 *
 * Each takes the writers it needs (`updateCombatantState`, the narrative
 * appenders) as arguments rather than closing over React state, so every
 * caller keeps its own dependency array and the helper stays a plain
 * function. Extracted from DeathWatchSimulator, where each was written out
 * two or three times; the log lines are unchanged, word for word.
 */
import { mergeCondition } from '../engine/conditions';
import { dropOnNonAttackAction } from '../engine/preparedStates';

/** Does this condition list hold a condition of type `code`? The engine's (conditions.ts), re-exported here. */
export { hasCondition } from '../engine/conditions';

/** `conds` with every code merged in under the table's stacking policy. */
export function mergeAll(conds, codes, stackPolicy) {
  const next = [...conds];
  for (const code of codes) mergeCondition(next, code, stackPolicy);
  return next;
}

/**
 * Strip the conditions an event ends - `expire(conds)` answers
 * `{ conds, removed }`, as `engine/preparedStates` does - and write the
 * survivors back. Returns the removed labels joined for a log line
 * ("Aiming (Half) and Feint"), or null when nothing was held.
 */
export function expireConditions(combatant, expire, updateCombatantState) {
  const out = expire(combatant.state?.conditions ?? []);
  if (!out.removed.length) return null;
  updateCombatantState(combatant.id, (st) => ({ ...st, conditions: expire(st.conditions ?? []).conds }));
  return out.removed.join(' and ');
}

/**
 * RAW: anything that is not an attack loses a held aim (Ah/Af). Writes the
 * actor's conditions and says so, or does nothing when there was no aim.
 *
 * @param {object} actor - the acting combatant
 * @param {string} why - the end of the log line: "reloading is not attacking."
 */
export function dropHeldAim(actor, why, { updateCombatantState, addRollNarrative }) {
  const lost = expireConditions(actor, dropOnNonAttackAction, updateCombatantState);
  if (lost) addRollNarrative(infoLine(`⏱ ${lost} lost - ${why}`));
}

/** Acting as nobody, aim is a raw toggle rather than a condition: lower it. */
export function clearRawAim(rawAttackToggles, setRawAttackToggles) {
  if (rawAttackToggles.aimHalf || rawAttackToggles.aimFull) {
    setRawAttackToggles((t) => ({ ...t, aimHalf: false, aimFull: false }));
  }
}

/** One plain info line, stamped now - the shape nearly every shell log line takes. */
export function infoLine(text) {
  return { type: 'info', text, timestamp: Date.now() };
}
